import { motion } from 'motion/react';
import type { SceneSection } from '../manifest';
import type { StylePack } from '../style-packs';

interface GhibliPresentationSectionProps {
  section: SceneSection;
  pack: StylePack;
  showCard: boolean;
  setShowCard: (show: boolean) => void;
}

const SAPTAPADI_VOWS = [
  { step: '१', title: 'Nourishment', desc: 'To provide food and sustenance' },
  { step: '२', title: 'Strength', desc: 'To nurture vigor & health' },
  { step: '३', title: 'Prosperity', desc: 'To share righteous wealth' },
  { step: '४', title: 'Joy', desc: 'To bring mutual happiness' },
  { step: '५', title: 'Family', desc: 'To care for parents & children' },
  { step: '६', title: 'Longevity', desc: 'To stand together all seasons' },
  { step: '⑦', title: 'Eternity', desc: 'An unbreakable bond of love' },
];

function VowBadge({
  vow,
  index,
}: {
  vow: (typeof SAPTAPADI_VOWS)[0];
  index: number;
}) {
  return (
    <motion.div
      initial={{ y: 24, opacity: 0, scale: 0.88 }}
      whileInView={{ y: 0, opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, delay: 0.2 + index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      className="p-3 rounded-xl text-center flex flex-col items-center justify-center bg-amber-400/15 border border-amber-400/35 text-amber-200 font-sans backdrop-blur-md shadow-md transition-all hover:bg-amber-400/25 hover:scale-105"
    >
      <div className="w-6 h-6 rounded-full bg-amber-400 text-black font-black text-xs flex items-center justify-center mb-1 shadow">
        {vow.step}
      </div>
      <p className="text-[11px] font-bold leading-tight text-white">{vow.title}</p>
      <p className="text-[9px] opacity-80 text-amber-200 mt-0.5 line-clamp-1">{vow.desc}</p>
    </motion.div>
  );
}

export function GhibliPresentationSection({
  section,
  pack,
  showCard,
  setShowCard,
}: GhibliPresentationSectionProps) {
  const moment =
    section.momentId ??
    (section.order === 0
      ? 'engagement'
      : section.order === 1
      ? 'haldi'
      : section.order === 2
      ? 'antarpat'
      : section.order === 3
      ? 'varmala'
      : 'saptapadi');

  // Base card styling with high-refraction translucent frosted glass and high contrast
  const baseCardStyle = {
    backgroundColor: 'rgba(15, 10, 24, 0.72)',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
    border: '1.5px solid rgba(251, 191, 36, 0.45)',
    boxShadow: '0 24px 70px rgba(0,0,0,0.85), inset 0 0 25px rgba(251, 191, 36, 0.12)',
    color: '#FEFCE8',
  };

  const titleStyle = {
    fontFamily: pack.fonts.display,
    color: '#FFFFFF',
    textShadow: '0 2px 16px rgba(0,0,0,0.95), 0 0 24px rgba(245, 158, 11, 0.4)',
  };

  const subtitleStyle = {
    fontFamily: pack.fonts.script ?? pack.fonts.display,
    color: '#FDE68A',
    textShadow: '0 2px 10px rgba(0,0,0,0.8)',
  };

  const chipStyle = 'bg-amber-400/15 border border-amber-400/35 text-amber-200 font-sans backdrop-blur-md shadow-md';

  const toggleButton = (
    <button
      onClick={() => setShowCard(!showCard)}
      className="absolute -top-3.5 right-4 z-40 flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-black/90 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-amber-300 shadow-xl backdrop-blur-md transition-all hover:scale-105 active:scale-95 cursor-pointer pointer-events-auto"
      title="Toggle pure artwork showcase mode"
    >
      <span>{showCard ? '👁️ VIEW ARTWORK' : '✨ SHOW DETAILS'}</span>
    </button>
  );

  return (
    <div className="relative z-10 w-full min-h-screen flex pointer-events-none">
      <style>{`
        @keyframes ppt-float-badge {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        @keyframes ppt-aura-pulse {
          0%, 100% { box-shadow: 0 0 15px rgba(245, 158, 11, 0.25); }
          50% { box-shadow: 0 0 35px rgba(245, 158, 11, 0.65); }
        }
      `}</style>

      {/* ───────────────────────────────────────────────────────────────────────────
          BLOCK 1: SAGAI DASTOOR — Left-Docked Card with PowerPoint Slide-in & Laser Rule
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'engagement' && (
        <div className="w-full max-w-7xl mx-auto flex items-center justify-start px-6 md:px-16 py-12">
          <motion.div
            initial={{ x: -120, opacity: 0, scale: 0.92 }}
            whileInView={{ x: 0, opacity: showCard ? 1 : 0.05, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-lg w-full pointer-events-auto transition-opacity duration-300"
          >
            {toggleButton}

            {/* Left Vertical Glowing Timeline Laser Rule (draws down into place) */}
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformOrigin: 'top' }}
              className="absolute -left-3 md:-left-5 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 via-amber-500 to-transparent rounded-full shadow-[0_0_10px_#F59E0B]"
            />
            <div
              className="absolute -left-4 md:-left-6 -top-1 w-3.5 h-3.5 rounded-full bg-amber-400 shadow-[0_0_12px_#F59E0B]"
              style={{ animation: 'ppt-aura-pulse 2s infinite' }}
            />

            <div style={baseCardStyle} className="p-7 md:p-9 rounded-2xl relative">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase font-bold text-amber-400">
                  ✦ ACT I · SAGAI DASTOOR ✦
                </span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold leading-tight mb-2" style={titleStyle}>
                {section.content.title}
              </h2>

              {section.content.subtitle && (
                <p className="text-lg md:text-2xl mb-5 font-semibold" style={subtitleStyle}>
                  {section.content.subtitle}
                </p>
              )}

              {/* PowerPoint Cascading Detail Chips */}
              <div className="mt-4 flex flex-col gap-2.5">
                <motion.div
                  initial={{ x: -45, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl ${chipStyle}`}
                >
                  <span className="text-base">📅</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest opacity-70">Auspicious Date</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ x: -45, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: 0.38, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl ${chipStyle}`}
                >
                  <span className="text-base">⏰</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest opacity-70">Golden Hour Mahurat</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.time || '05:00 PM Sundown'}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ x: -45, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: 0.48, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl ${chipStyle}`}
                >
                  <span className="text-base">📍</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest opacity-70">Royal Pavilion</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.venue?.name || 'Lake Palace Pavilion, Udaipur'}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          BLOCK 2: PITHHI HALDI — Wide Cinema Lower-Third Banner with PowerPoint Push-Up
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'haldi' && (
        <div className="w-full flex items-end justify-center pb-8 md:pb-12 px-4 md:px-10">
          <motion.div
            initial={{ y: 120, opacity: 0, scale: 0.92 }}
            whileInView={{ y: 0, opacity: showCard ? 1 : 0.05, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-5xl w-full pointer-events-auto transition-opacity duration-300"
          >
            {toggleButton}

            <div
              style={baseCardStyle}
              className="p-6 md:p-8 rounded-2xl md:rounded-3xl grid grid-cols-1 md:grid-cols-3 gap-6 items-center shadow-2xl"
            >
              {/* Column 1: Haldi Theme & Title */}
              <div className="md:border-r border-amber-500/25 md:pr-6">
                <div className="inline-flex items-center gap-2 mb-1.5 text-amber-400 text-xs font-bold tracking-widest uppercase">
                  <span>☀️</span>
                  <span>✦ ACT II · PITHHI HALDI ✦</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold leading-tight" style={titleStyle}>
                  {section.content.title}
                </h2>
                {section.content.subtitle && (
                  <p className="text-lg md:text-xl font-medium mt-1" style={subtitleStyle}>
                    {section.content.subtitle}
                  </p>
                )}
              </div>

              {/* Column 2: Auspicious Time Pill with Pulse */}
              <div className="flex flex-col items-center justify-center text-center px-2">
                <motion.div
                  initial={{ scale: 0.85, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/25 via-yellow-400/25 to-amber-500/25 border border-amber-400/50 shadow-inner"
                >
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-0.5">Morning Mahurat</p>
                  <p className="text-base md:text-xl font-black text-amber-400 tracking-wide font-serif">
                    {section.content.time || '10:00 AM'}
                  </p>
                </motion.div>
                {section.content.customNote && (
                  <p className="text-xs italic mt-2.5 opacity-90 font-serif text-amber-300">
                    {section.content.customNote}
                  </p>
                )}
              </div>

              {/* Column 3: Venue & Date */}
              <div className="md:border-l border-amber-500/25 md:pl-6 flex flex-col justify-center">
                <p className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Celebration Venue</p>
                <p className="text-sm md:text-base font-bold mb-1 text-white">{section.content.venue?.name || 'Courtyard Gardens, Udaipur'}</p>
                <p className="text-xs opacity-80 text-amber-200">{section.content.date}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          BLOCK 3: GATHBANDHAN & ANTARPAT — Right-Docked Pillar with PowerPoint Slide
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'antarpat' && (
        <div className="w-full max-w-7xl mx-auto flex items-center justify-end px-6 md:px-16 py-12">
          <motion.div
            initial={{ x: 120, opacity: 0, scale: 0.92 }}
            whileInView={{ x: 0, opacity: showCard ? 1 : 0.05, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-lg w-full pointer-events-auto transition-opacity duration-300"
          >
            {toggleButton}

            {/* Right Vertical Glowing Silk Knot Line */}
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformOrigin: 'top' }}
              className="absolute -right-3 md:-right-5 top-0 bottom-0 w-1 bg-gradient-to-b from-rose-400 via-amber-400 to-transparent rounded-full shadow-[0_0_10px_#FB7185]"
            />
            <div
              className="absolute -right-4 md:-right-6 -top-1 w-3.5 h-3.5 rounded-full bg-rose-400 shadow-[0_0_12px_#FB7185]"
              style={{ animation: 'ppt-aura-pulse 2s infinite' }}
            />

            <div style={baseCardStyle} className="p-7 md:p-9 rounded-2xl relative">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase font-bold text-amber-400">
                  ✦ ACT III · SACRED UNION ✦
                </span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold leading-tight mb-2" style={titleStyle}>
                {section.content.title}
              </h2>

              {section.content.subtitle && (
                <p className="text-lg md:text-2xl mb-4 font-semibold" style={subtitleStyle}>
                  {section.content.subtitle}
                </p>
              )}

              {/* Sacred Vow Poetic Callout */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="p-4 rounded-xl bg-amber-500/15 border-l-2 border-amber-400 my-4 text-xs md:text-sm italic font-serif text-amber-100 opacity-95 leading-relaxed shadow-sm"
              >
                &ldquo;Two souls woven together in timeless destiny under celestial skies, where the sacred curtain falls and true love begins.&rdquo;
              </motion.div>

              <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-amber-500/20 text-xs">
                <div>
                  <p className="opacity-70 uppercase tracking-widest text-[9px]">Auspicious Hour</p>
                  <p className="font-bold text-sm text-amber-300 font-serif">{section.content.time || '11:30 AM'}</p>
                </div>
                <div>
                  <p className="opacity-70 uppercase tracking-widest text-[9px]">Venue</p>
                  <p className="font-bold text-sm truncate text-white">{section.content.venue?.name || 'Royal Mandap'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          BLOCK 4: VARMALA — Floating Center Island with PowerPoint Zoom & Badge
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'varmala' && (
        <div className="w-full flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ y: 60, scale: 0.86, opacity: 0 }}
            whileInView={{ y: 0, scale: 1, opacity: showCard ? 1 : 0.05 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-xl w-full pointer-events-auto text-center transition-opacity duration-300"
          >
            {toggleButton}

            {/* Overhead Badge with Subtle Float Animation */}
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ animation: 'ppt-float-badge 4s ease-in-out infinite' }}
              className="mb-4 inline-flex items-center gap-2 px-5 py-2 rounded-full border border-amber-400/50 bg-black/80 backdrop-blur-md shadow-xl"
            >
              <span className="text-amber-300 text-xs tracking-[0.25em] font-serif uppercase font-bold">
                🌸 TORAN & VARMALA MAHORAT 🌸
              </span>
            </motion.div>

            <div style={baseCardStyle} className="p-8 md:p-10 rounded-3xl relative shadow-2xl">
              <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-3" style={titleStyle}>
                {section.content.title}
              </h2>

              {section.content.subtitle && (
                <p className="text-xl md:text-3xl mb-6 font-semibold" style={subtitleStyle}>
                  {section.content.subtitle}
                </p>
              )}

              <div
                className="my-6 inline-flex flex-col items-center px-8 py-3.5 rounded-2xl bg-amber-500/20 border border-amber-400/50 shadow-inner"
                style={{ animation: 'ppt-aura-pulse 3s infinite' }}
              >
                <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">Twilight Garland Exchange</span>
                <span className="text-2xl md:text-3xl font-black text-amber-400 font-serif">
                  {section.content.time || '07:30 PM'}
                </span>
              </div>

              <p className="text-sm md:text-base font-medium opacity-95 max-w-md mx-auto text-white">
                📍 {section.content.venue?.name || 'Grand Palace Courtyard, Udaipur'}
              </p>
              <p className="text-xs uppercase tracking-widest opacity-80 mt-1 text-amber-200">{section.content.date}</p>
            </div>
          </motion.div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          BLOCK 5: SAAT PHERE — Grand Stage with PowerPoint 7-Vows Stepper
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'saptapadi' && (
        <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center px-4 md:px-8 py-10">
          <motion.div
            initial={{ y: 80, scale: 0.9, opacity: 0 }}
            whileInView={{ y: 0, scale: 1, opacity: showCard ? 1 : 0.05 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full pointer-events-auto transition-opacity duration-300"
          >
            {toggleButton}

            {/* Top Grand Seal Header */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full border border-amber-400/50 bg-black/70 backdrop-blur-md shadow-lg mb-2">
                <span className="text-amber-300 text-xs tracking-[0.25em] font-serif uppercase font-bold">
                  ✦ ACT V · SACRED VOWS · SAAT PHERE ✦
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={titleStyle}>
                {section.content.title}
              </h2>
            </div>

            {/* Circumambulation Progress Track */}
            <div className="w-full max-w-md mx-auto my-3 px-2">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1.5">
                <span>🔥 Sacred Agni Parikrama</span>
                <span className="opacity-90">Seven Eternal Steps</span>
              </div>
              <div className="w-full h-2 rounded-full bg-black/60 border border-amber-400/40 overflow-hidden shadow-inner">
                <motion.div
                  initial={{ width: '0%' }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 shadow-[0_0_12px_#F59E0B]"
                />
              </div>
            </div>

            {/* 7 Sacred Vows Stepper with Cascading PowerPoint Entry */}
            <div className="my-5 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {SAPTAPADI_VOWS.map((vow, idx) => (
                <VowBadge key={vow.step} vow={vow} index={idx} />
              ))}
            </div>

            {/* Bottom Mandap & Location Card */}
            <motion.div
              initial={{ y: 35, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="p-6 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left"
              style={baseCardStyle}
            >
              <div>
                <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Sacred Agni Mandap</p>
                <p className="text-sm md:text-base font-bold text-white">{section.content.venue?.name || 'City Palace Royal Mandap, Udaipur'}</p>
                <p className="text-xs opacity-85 text-amber-200">{section.content.date} · {section.content.time || '09:00 PM'}</p>
              </div>
              <div className="px-5 py-2.5 rounded-full bg-amber-400 text-black text-xs font-black uppercase tracking-wider shadow-lg">
                Padharo Mhare Des ✨
              </div>
            </motion.div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
